import React, { useEffect, useState, useRef, useCallback } from "react";
import { useLang } from "../context/LanguageContext";
import api, { API } from "../lib/api";
import { Instagram, Play } from "lucide-react";

const FALLBACK_IMAGES = [
  { url: "https://images.unsplash.com/photo-1638337935003-e17cf483ca8d?w=800&q=80", category: "Lifestyle", title: "Editorial White" },
  { url: "https://images.unsplash.com/photo-1677325188706-160d347e3865?w=800&q=80", category: "Events", title: "Live Concert" },
  { url: "https://images.unsplash.com/photo-1761701391167-863560b27f5e?w=800&q=80", category: "Brands", title: "Studio Brand Film" },
  { url: "https://images.unsplash.com/photo-1751062190352-721bfdc6f0d6?w=800&q=80", category: "Artists", title: "Light Study" },
  { url: "https://images.unsplash.com/photo-1761155228019-0a29e3f6c8e9?w=800&q=80", category: "Events", title: "Stage" },
  { url: "https://images.unsplash.com/photo-1638337935003-e17cf483ca8d?w=800&q=80", category: "Luxury", title: "Couture" },
];

const INITIAL_BATCH = 16;

// Single portfolio cell - uses IntersectionObserver to decide when to load
function PortfolioItem({ item, idx, isFallback }) {
  const ref = useRef(null);
  const videoRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [hovered, setHovered] = useState(false);

  const isVideo = !isFallback && item.media_type === "video";
  const thumbSrc = isFallback
    ? item.url
    : isVideo
      ? `${API}/files/${item.id}?poster=true`
      : `${API}/files/${item.id}?w=800`;
  const fullSrc = isFallback ? item.url : `${API}/files/${item.id}`;

  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setVisible(true);
            obs.disconnect();
          }
        });
      },
      { rootMargin: "200px" }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  // Pause video when not hovered
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (hovered) {
      v.play().catch(() => {});
    } else {
      v.pause();
    }
  }, [hovered]);

  const aspect =
    idx % 5 === 0 ? "aspect-[3/4]" : idx % 4 === 0 ? "aspect-square" : "aspect-[4/5]";
  const span = idx % 7 === 0 ? "md:col-span-2 md:row-span-2" : "";

  return (
    <div
      ref={ref}
      className={`portfolio-item ${aspect} ${span} bg-zinc-900`}
      data-testid={`portfolio-item-${idx}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {visible && (
        <>
          {/* Always show thumbnail (poster for videos, optimized 800px for images) */}
          <img
            src={thumbSrc}
            alt={item.title || ""}
            loading="lazy"
            decoding="async"
            className={`w-full h-full object-cover absolute inset-0 ${
              isVideo && hovered ? "opacity-0" : "opacity-100"
            } transition-opacity duration-300`}
          />
          {/* Video only loaded once user hovers */}
          {isVideo && hovered && (
            <video
              ref={videoRef}
              src={fullSrc}
              poster={thumbSrc}
              muted
              loop
              playsInline
              preload="metadata"
              className="w-full h-full object-cover absolute inset-0"
            />
          )}
          {isVideo && (
            <div className="absolute top-3 right-3 bg-black/70 backdrop-blur-sm rounded-full p-2 z-10">
              <Play size={14} className="text-white" />
            </div>
          )}
        </>
      )}

      <div className="portfolio-overlay">
        <div>
          <p className="font-mono-tight text-zinc-400 mb-1">{item.category}</p>
          <p className="text-white text-lg font-light">{item.title || "Untitled"}</p>
        </div>
      </div>
    </div>
  );
}

export default function Portfolio() {
  const { t } = useLang();
  const [filter, setFilter] = useState(t.portfolio.categories[0]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCount, setShowCount] = useState(INITIAL_BATCH);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/portfolio")
      .then(({ data }) => {
        if (!cancelled) setItems(data || []);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // when language changes, reset filter to "All/Tous"
  useEffect(() => {
    setFilter(t.portfolio.categories[0]);
    setShowCount(INITIAL_BATCH);
  }, [t]);

  const allItem = t.portfolio.categories[0];
  const usingFallback = items.length === 0;

  const baseList = usingFallback ? FALLBACK_IMAGES : items;
  const filtered =
    filter === allItem ? baseList : baseList.filter((i) => i.category === filter);
  const displayed = filtered.slice(0, showCount);
  const hasMore = filtered.length > showCount;

  const loadMore = useCallback(() => {
    setShowCount((c) => c + INITIAL_BATCH);
  }, []);

  return (
    <section
      id="work"
      data-testid="portfolio-section"
      className="relative bg-[#050505] py-24 md:py-32 px-6 md:px-12 border-t border-white/5"
    >
      <div className="max-w-[1600px] mx-auto">
        <div className="grid grid-cols-12 gap-8 mb-12">
          <div className="col-span-12 md:col-span-6">
            <p className="font-mono-tight text-zinc-500 mb-6">{t.portfolio.label}</p>
            <h2
              className="text-5xl md:text-7xl font-display tracking-tighter text-white"
              data-testid="portfolio-title"
            >
              {t.portfolio.title}
            </h2>
          </div>
          <div className="col-span-12 md:col-span-6 flex md:items-end md:justify-end">
            <a
              href="https://www.instagram.com/salside.production"
              target="_blank"
              rel="noopener noreferrer"
              className="font-mono-tight text-zinc-400 hover:text-white inline-flex items-center gap-2"
              data-testid="portfolio-ig-link"
            >
              <Instagram size={16} />
              @salside.production
            </a>
          </div>
        </div>

        {/* Categories */}
        <div
          className="flex flex-wrap gap-2 md:gap-3 mb-10 border-y border-white/10 py-5"
          data-testid="portfolio-filters"
        >
          {t.portfolio.categories.map((c) => (
            <button
              key={c}
              onClick={() => {
                setFilter(c);
                setShowCount(INITIAL_BATCH);
              }}
              data-testid={`portfolio-filter-${c.toLowerCase()}-btn`}
              className={`font-mono-tight px-4 py-2 transition-all ${
                filter === c ? "bg-white text-black" : "text-zinc-500 hover:text-white"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {/* Grid */}
        {loading ? (
          <div className="text-zinc-500 font-mono-tight">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="py-20 text-center" data-testid="portfolio-empty">
            <p className="text-zinc-400 max-w-xl mx-auto mb-8">{t.portfolio.empty}</p>
            <a
              href="https://www.instagram.com/salside.production"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-ghost inline-flex"
            >
              <Instagram size={16} /> {t.portfolio.visit_ig}
            </a>
          </div>
        ) : (
          <>
            <div
              className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 md:gap-3"
              data-testid="portfolio-grid"
            >
              {displayed.map((item, idx) => (
                <PortfolioItem
                  key={item.id || `fb-${idx}`}
                  item={item}
                  idx={idx}
                  isFallback={usingFallback}
                />
              ))}
            </div>
            {hasMore && (
              <div className="flex justify-center mt-12">
                <button
                  onClick={loadMore}
                  className="btn-ghost"
                  data-testid="portfolio-load-more-btn"
                >
                  Voir plus ({filtered.length - showCount})
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
